/**
 * Statistiques de musculation (V2 §6.2, specs §12.5).
 *
 * Les six métriques retenues au §6.2, et l'ordre suit ce qu'on vient chercher :
 *
 *   1. **Volume par semaine** — combien de séries de travail, et quand.
 *   2. **Répartition par muscle** — où va le travail, sur toute la plage.
 *   3. **À muscle constant** — volume et tonnage de celui qu'on désigne.
 *   4. **RPE moyen par séance** — à quel prix.
 *   5. **Records estimés** — jamais des records.
 *
 * 🔴 **Le tonnage ne se lit qu'à muscle CONSTANT** (§6.2) : « 100 kg de squat et
 * 100 kg de curl ne sont pas la même chose ». Il n'apparaît donc jamais comme un
 * total tous exercices confondus, mais uniquement dans le bloc 3, pour le muscle
 * qu'on a sélectionné — et il y est empilé **sous** le volume, dans son propre
 * cadre, jamais superposé sur un second axe.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { StatsRange } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchTrainingStats, trainingStatsKey } from '../../api/stats';
import { Card } from '../../components/nourri';
import { Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { RangeRow, RankedBars, TimeChart, WeekBars } from './StatCharts';

export function TrainingSection() {
  const [range, setRange] = useState<StatsRange>('90d');

  const stats = useQuery({
    queryKey: trainingStatsKey(range),
    queryFn: () => fetchTrainingStats(range),
    placeholderData: (previous) => previous,
  });

  /*
   * ⚠️ **Le muscle désigné est un état LOCAL, et c'est légitime.** La règle du
   * projet (« sections et sous-pages sont des routes ») porte sur ce qui a une
   * **adresse** ; un filtre à l'intérieur d'un écran n'en est pas une, pas plus
   * que la plage du graphique de poids ne l'est depuis l'étape 8.
   */
  const [muscle, setMuscle] = useState<string | null>(null);

  const lecture = readError(stats);
  const data = stats.data;

  /*
   * ⚠️ **Le muscle retenu par défaut est le premier de la liste**, c'est-à-dire
   * le plus travaillé — dérivé au rendu, jamais posé dans un effet. Un
   * `useEffect` de pré-sélection serait refusé par `react-hooks/
   * set-state-in-effect`, à juste titre : il ferait un rendu de plus, et il
   * garderait un muscle absent de la plage après un changement de période.
   */
  const muscleAffiche =
    muscle !== null && data?.muscles.includes(muscle) === true
      ? muscle
      : (data?.muscles[0] ?? null);

  return (
    <>
      <RangeRow range={range} onChange={setRange} />
      <ReadNote error={lecture} quoi="les statistiques de musculation" />
      {data === undefined && lecture === null && <Hint>Chargement…</Hint>}

      {data && (
        <div style={stats.isPlaceholderData ? { opacity: 0.45 } : undefined}>
          <Card>
            <div className="n-eyebrow">Séries de travail par semaine</div>
            <div className="n-chart">
              <WeekBars
                unite="séries"
                barres={data.weeks.map((week) => ({
                  cle: week.weekStart,
                  valeur: week.workSets,
                  detail: `${String(week.sessions)} séance${week.sessions > 1 ? 's' : ''}`,
                }))}
              />
            </div>
            <p className="n-hint" style={{ marginTop: 10 }}>
              {String(data.sessions)} séance{data.sessions > 1 ? 's' : ''} et{' '}
              {String(data.workSets)} séries de travail du {data.from} au {data.to}. Les
              échauffements, les dégressives et les séries jusqu’à l’échec ne sont pas comptés
              ici — un échauffement ne fait pas travailler, et une dégressive descend la charge
              exprès.
            </p>
          </Card>

          <Card>
            <div className="n-eyebrow">Répartition par groupe musculaire</div>
            {/*
              🔴 **Une liste de barres, jamais un empilement coloré.** Dix-sept
              muscles auraient demandé dix-sept teintes catégorielles, alors que
              la palette du projet en compte quatre — et que ces quatre-là ne se
              distinguent déjà pas assez (ΔE 2,5 en deutéranopie) pour porter une
              identité seules. Ici c'est le **libellé** qui identifie : rien à
              mesurer, rien à confondre.
            */}
            <RankedBars
              unite="séries"
              selection={muscleAffiche}
              onSelect={setMuscle}
              lignes={data.frequency.map((row) => ({
                cle: row.muscle,
                libelle: row.muscle,
                valeur: row.sets,
              }))}
            />
            <p className="n-hint" style={{ marginTop: 10 }}>
              Un muscle sollicité en <strong>secondaire</strong> compte pour une demi-série : le développé
              couché ne travaille pas les triceps autant que les pectoraux, mais il les
              travaille. C’est la même pondération que la carte corporelle d’une routine.
              {data.unattributedSets > 0 && (
                <>
                  {' '}
                  ⚠️ {String(data.unattributedSets)} série
                  {data.unattributedSets > 1 ? 's' : ''} ne{' '}
                  {data.unattributedSets > 1 ? 'sont' : 'est'} rattachée
                  {data.unattributedSets > 1 ? 's' : ''} à aucun muscle — exercice supprimé, ou
                  exercice personnel dont les muscles sont saisis autrement.
                </>
              )}
            </p>
          </Card>

          {muscleAffiche !== null && (
            <Card>
              <div className="n-eyebrow">{muscleAffiche} — semaine par semaine</div>
              {/*
                🔴 **Deux cadres empilés, jamais deux axes superposés.** Des
                séries et des kilos n'ont pas la même unité : les superposer
                ferait se croiser deux courbes dont seule la mise à l'échelle
                aurait écrit l'histoire. Ils partagent l'axe du temps — ce qui
                est vrai, ce sont les mêmes semaines — et rien d'autre.
              */}
              <div className="n-chart">
                <WeekBars
                  unite="séries"
                  barres={data.weeks
                    .map((week) => ({
                      cle: week.weekStart,
                      valeur: week.byMuscle.find((m) => m.muscle === muscleAffiche)?.sets ?? 0,
                    }))
                    .filter((b) => b.valeur > 0)}
                />
              </div>
              <div className="n-chart" style={{ marginTop: 14 }}>
                <WeekBars
                  unite="kg"
                  couleur="var(--ink-soft)"
                  barres={data.weeks
                    .map((week) => ({
                      cle: week.weekStart,
                      valeur: week.byMuscle.find((m) => m.muscle === muscleAffiche)?.tonnage ?? 0,
                    }))
                    .filter((b) => b.valeur > 0)}
                />
              </div>
              <p className="n-hint" style={{ marginTop: 10 }}>
                En haut les séries, en bas le tonnage (répétitions × charge, en kg). Le tonnage
                ne se compare <strong>jamais d’un muscle à l’autre</strong> — il ne veut dire quelque chose
                que suivi dans le temps, à muscle constant. Touchez un muscle dans la liste
                ci-dessus pour changer.
              </p>
            </Card>
          )}

          <Card>
            <div className="n-eyebrow">RPE moyen par séance</div>
            <div className="n-chart">
              <TimeChart
                dates={data.rpe.map((r) => r.date)}
                unite="RPE"
                formater={(v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
                traces={[
                  {
                    libelle: 'RPE moyen',
                    couleur: 'var(--brand)',
                    valeurs: data.rpe.map((r) => r.rpe),
                  },
                ]}
              />
            </div>
            <p className="n-hint" style={{ marginTop: 10 }}>
              Moyenne des RPE réellement saisis sur les séries de travail. Une série sans RPE
              n’est pas comptée comme un zéro — elle n’est pas comptée du tout.
            </p>
          </Card>

          <Card>
            <div className="n-eyebrow">Maximums estimés</div>
            {/*
              🔴 **« Estimé », et le mot n'est pas décoratif.** La formule d'Epley
              (charge × (1 + reps ÷ 30)) est plafonnée à 12 répétitions : au-delà
              elle diverge et produit des nombres qui ne veulent rien dire. Un
              1RM calculé et un 1RM soulevé ne sont pas la même chose, et le
              second n'est jamais mesuré ici.
            */}
            <RankedBars
              unite="kg"
              lignes={data.estimatedMaxes.slice(0, 12).map((max) => ({
                cle: max.exerciseClientId,
                libelle: max.exerciseName,
                valeur: max.estimatedKg,
              }))}
            />
            <p className="n-hint" style={{ marginTop: 10 }}>
              Estimations, jamais des records : calculées par la formule d’Epley à partir de la
              meilleure série de la période, et seulement jusqu’à 12 répétitions — au-delà, la
              formule cesse de vouloir dire quelque chose. Aucun de ces poids n’a forcément été
              soulevé une seule fois.
            </p>
          </Card>
        </div>
      )}
    </>
  );
}
